import { userType } from "constants-lib/authentication"
import { PRODUCT_LOBS } from "constants-lib/common"
import { USER_STATES } from "constants-lib/userState"

/**
 * Constants related to user states and products.
 * @module userStates
 */

/**
 * An array of invalid user LOBs (Line of Business).
 * @type {Array<string>}
 */
export const INVALID_USER_LOBS = [undefined, null, "", "NA", "LANDFILL"]

/**
 * Map of user products to their corresponding LOBs (Line of Business).
 * @typedef {Object} USER_PRODUCTS_MAP
 * @property {string} SMB - Small and Medium Business LOB.
 * @property {string} OMR - Residential LOB.
 * @property {string} TRO - Roll-off LOB.
 * @property {string} COMMERCIAL - Commercial LOB.
 * @property {string} RESIDENTIAL - Residential LOB.
 * @property {string} ROLLOFF - Roll-off LOB.
 */

/**
 * Object containing the mapping of user products to their corresponding LOBs.
 * @type {USER_PRODUCTS_MAP}
 */
export const USER_PRODUCTS_MAP = {
  SMB: PRODUCT_LOBS.COMMERCIAL,
  OMR: PRODUCT_LOBS.RESIDENTIAL,
  TRO: PRODUCT_LOBS.ROLLOFF,
  COMMERCIAL: PRODUCT_LOBS.COMMERCIAL,
  RESIDENTIAL: PRODUCT_LOBS.RESIDENTIAL,
  ROLLOFF: PRODUCT_LOBS.ROLLOFF,
}

/**
 * Map of user types to their corresponding states.
 * @typedef {Object} USER_STATE_CONDITION_MAP
 * @property {string} ANONYMOUS - Anonymous user state.
 * @property {string} PROSPECTIVE - Prospective user state.
 * @property {string} EXISTING - Existing user state.
 * @property {string} COOKIED - Cookied user state.
 * @property {string} LOGGED_IN - Logged-in user state.
 */

/**
 * Object containing the mapping of user types to their corresponding states.
 * @type {USER_STATE_CONDITION_MAP}
 */
export const USER_STATE_CONDITION_MAP = {
  [userType.ANONYMOUS]: USER_STATES.NEW_USER,
  [userType.PROSPECTIVE]: USER_STATES.NEW_USER,
  [userType.EXISTING]: USER_STATES.ACCOUNT_DETECTED,
  [userType.COOKIED]: USER_STATES.COOKIED,
  [userType.LOGGED_IN]: USER_STATES.LOGGED_IN,
  /* conditions for cookied users */
  [`${userType.COOKIED}${[PRODUCT_LOBS.RESIDENTIAL]}`]:
    USER_STATES.RETURNING_OMR_AVAILABLE,
  [`${userType.COOKIED}${[PRODUCT_LOBS.COMMERCIAL]}`]:
    USER_STATES.RETURNING_SMB_AVAILABLE,
  [`${userType.COOKIED}${[PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.RETURNING_TRO_AVAILABLE,
  [`${userType.COOKIED}${[PRODUCT_LOBS.RESIDENTIAL, PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.RETURNING_OMR_TRO,
  [`${userType.COOKIED}${[PRODUCT_LOBS.COMMERCIAL, PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.RETURNING_SMB_TRO,
  [`${userType.COOKIED}${[PRODUCT_LOBS.COMMERCIAL, PRODUCT_LOBS.RESIDENTIAL]}`]:
    USER_STATES.RETURNING_ALL_AVAILABLE,
  [`${userType.COOKIED}${[
    PRODUCT_LOBS.COMMERCIAL,
    PRODUCT_LOBS.RESIDENTIAL,
    PRODUCT_LOBS.ROLLOFF,
  ]}`]: USER_STATES.RETURNING_ALL_AVAILABLE,
  /* conditions for logged-in users ...get from linkedAccounts */
  [`${userType.LOGGED_IN}${[PRODUCT_LOBS.RESIDENTIAL]}`]:
    USER_STATES.LOGGED_IN_OMR_AVAILABLE,
  [`${userType.LOGGED_IN}${[PRODUCT_LOBS.COMMERCIAL]}`]:
    USER_STATES.LOGGED_IN_SMB_AVAILABLE,
  [`${userType.LOGGED_IN}${[PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.LOGGED_IN_TRO_AVAILABLE,
  [`${userType.LOGGED_IN}${[PRODUCT_LOBS.RESIDENTIAL, PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.LOGGED_IN_OMR_TRO,
  [`${userType.LOGGED_IN}${[PRODUCT_LOBS.COMMERCIAL, PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.LOGGED_IN_SMB_TRO,
  [`${userType.LOGGED_IN}${[
    PRODUCT_LOBS.COMMERCIAL,
    PRODUCT_LOBS.RESIDENTIAL,
  ]}`]: USER_STATES.LOGGED_IN_ALL_AVAILABLE,
  [`${userType.LOGGED_IN}${[
    PRODUCT_LOBS.COMMERCIAL,
    PRODUCT_LOBS.RESIDENTIAL,
    PRODUCT_LOBS.ROLLOFF,
  ]}`]: USER_STATES.LOGGED_IN_ALL_AVAILABLE,
  /* conditions for existing users */
  [`${userType.EXISTING}${[PRODUCT_LOBS.RESIDENTIAL]}`]:
    USER_STATES.ACCOUNT_DETECTED_OMR_AVAILABLE,
  [`${userType.EXISTING}${[PRODUCT_LOBS.COMMERCIAL]}`]:
    USER_STATES.ACCOUNT_DETECTED_SMB_AVAILABLE,
  [`${userType.EXISTING}${[PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.ACCOUNT_DETECTED_TRO_AVAILABLE,
  [`${userType.EXISTING}${[PRODUCT_LOBS.RESIDENTIAL, PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.ACCOUNT_DETECTED_OMR_TRO,
  [`${userType.EXISTING}${[PRODUCT_LOBS.COMMERCIAL, PRODUCT_LOBS.ROLLOFF]}`]:
    USER_STATES.ACCOUNT_DETECTED_SMB_TRO,
  [`${userType.EXISTING}${[
    PRODUCT_LOBS.COMMERCIAL,
    PRODUCT_LOBS.RESIDENTIAL,
  ]}`]: USER_STATES.ACCOUNT_DETECTED_ALL_AVAILABLE,
  [`${userType.EXISTING}${[
    PRODUCT_LOBS.COMMERCIAL,
    PRODUCT_LOBS.RESIDENTIAL,
    PRODUCT_LOBS.ROLLOFF,
  ]}`]: USER_STATES.ACCOUNT_DETECTED_ALL_AVAILABLE,
}
